import {
  useNotify,
  useModal,
  useViewport,
  generateSupportUrl,
  MediaObject,
  IconHelp,
  IconChat,
  Stack,
  Inline,
  Modal,
  LinkButton,
  Text,
} from '@treatwell/ui';
import React, { useRef } from 'react';
import { SalesForceTokens } from '@treatwell/ui/dist/types/src/hooks/useSalesforceBot/types';
import clsx from 'clsx';
import styles from './CustomSupportFooter.module.css';
import { useSupportChat } from './hooks/useSupportChat';
import { OneChatUIConfig, OneChatUserInfo } from './hooks/types';

type SupportStrings = {
  genericError: string; // Something went wrong, please try again
  helpCentre: string; // Help Centre
  chatSupport: string; // Chat support
  modal?: {
    title: string; // Have a question you need help with?
    body: string; // Log in to get help with your bookings, account, and more.
    cta: string; // Login or sign-up
    close: string; // Close
    linkText: string; // I can't login or sign-up
  };
};

export type CustomChatConfiguration = {
  bootstrapJsSrc: string;
  baseUrl: string;
  developerName: string;
  orgId: string;
};

type CustomSupportFooterProps = {
  configuration: CustomChatConfiguration;
  defaultOpen?: boolean;
  layout?: 'stack' | 'inline';
  loginUrl?: string;
  helpUrl?: string;
  onChatOpen?: (conversationId: string | null) => void;
  onChatClose?: (conversationId: string | null) => void;
  getTokens: () => Promise<SalesForceTokens | null>;
  onHandleChatError?: (error: Error) => void;
  userInfo: OneChatUserInfo;
  supportStrings: SupportStrings;
  userAuthenticated: boolean;
  locale: string;
  uiConfig?: OneChatUIConfig;
  initializeChatOnRender?: boolean;
};

export const CustomSupportFooter = ({
  configuration,
  layout = 'inline',
  loginUrl = '/protected-action/?route=/account?chat',
  helpUrl = 'https://customercare.treatwell.com/s/article/How-to-Request-Support-as-a-Guest-User-on-Treatwell',
  onChatOpen,
  onChatClose,
  getTokens,
  supportStrings,
  userAuthenticated,
  userInfo,
  locale,
  uiConfig,
  initializeChatOnRender = true,
}: CustomSupportFooterProps) => {
  const chatOpened = useRef(false);
  const shouldInitialize = initializeChatOnRender && userAuthenticated;

  const init = {
    developerName: configuration.developerName,
    orgId: configuration.orgId,
    baseUrl: configuration.baseUrl,
  };

  const getAccessToken = getTokenAdapter(getTokens, 'chatSessionToken');
  const getProfileAccessToken = getTokenAdapter(
    getTokens,
    'profileAccessToken'
  );

  const { isReady, isLoading, openChat, closeChat } = useSupportChat({
    bootstrapJsSrc: configuration.bootstrapJsSrc,
    initializeChatOnRender: shouldInitialize,
    configuration: {
      uiConfig,
      locale: userInfo.language || locale,
      userInfo,
      getAccessToken,
      getProfileAccessToken,
      ...init,
    },
  });
  const { notifyError } = useNotify();
  const { modalProps, openModal } = useModal({
    width: 650,
  });
  const isMobile = useViewport({
    device: 'mobile',
    serverRender: typeof window === 'undefined',
  });
  const supportUrl = generateSupportUrl(locale);
  const { genericError, helpCentre, chatSupport, modal } = supportStrings;

  const handleChatClick = async () => {
    if (!userAuthenticated) {
      openModal();
      return;
    }
    // TODO: add conversationId to the onChatOpen and onChatClose callbacks
    // once the widget supports the event listeners.
    try {
      if (chatOpened.current) {
        closeChat();
        onChatClose && onChatClose(null);
        chatOpened.current = false;
      } else {
        openChat();
        onChatOpen && onChatOpen(null);
        chatOpened.current = true;
      }
    } catch {
      notifyError(genericError);
    }
  };

  const footerItems = (
    <>
      <a href={supportUrl} className={styles.footerItem}>
        <MediaObject space={layout === 'stack' ? 'md' : 'xs'} align="center">
          <IconHelp size={24} />
          <Text type={isMobile ? 'caption' : 'bodyHeavy'}>{helpCentre}</Text>
        </MediaObject>
      </a>
      <button
        className={clsx(styles.footerItem, styles.button)}
        onClick={handleChatClick}
        disabled={shouldInitialize ? isLoading || !isReady : isLoading}
      >
        <MediaObject space={layout === 'stack' ? 'md' : 'xs'} align="center">
          <IconChat size={24} />
          <Text type={isMobile ? 'caption' : 'bodyHeavy'}>{chatSupport}</Text>
        </MediaObject>
      </button>
    </>
  );
  return (
    <>
      {layout === 'stack' ? (
        <Stack space="lg">{footerItems}</Stack>
      ) : (
        <Inline space="xl" align="center">
          {footerItems}
        </Inline>
      )}
      {modal && (
        <Modal className={styles.modal} {...modalProps}>
          <Modal.Header closeLabel="Close" />
          <Modal.Body>
            <Stack space="xxl" align="center">
              <Stack space="md" align="center" justify="center">
                <Text type="xlHeader">{modal.title}</Text>
                <Text type="body">{modal.body}</Text>
              </Stack>

              <Stack>
                <LinkButton
                  className={styles.loginButton}
                  buttonStyle="primary"
                  size="lg"
                  fullWidth
                  href={loginUrl}
                >
                  {modal.cta}
                </LinkButton>
                <Text type="caption">
                  <a href={helpUrl} className={styles.link}>
                    {modal.linkText}
                  </a>
                </Text>
              </Stack>
            </Stack>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

const getTokenAdapter = (
  getTokens: () => Promise<SalesForceTokens | null>,
  type: keyof SalesForceTokens
) => {
  return async () => {
    try {
      const tokens = await getTokens();
      return tokens ? tokens[type] || null : null;
    } catch (err) {
      return null;
    }
  };
};
